// src/map/plateOverlay/ownshipOverlay.ts
//
// Render the ownship triangle on top of the active plate raster.
//
// Active only when:
//   - a plate overlay is active
//   - the plate georef resolved as 'ok' (not low-confidence / fallback)
//   - the user has the toggle ON (plateOverlayShowOwnship)
//
// GPS updates are gated: only calls setData when the fix has moved >100 m
// OR heading changed >2° since the last update, avoiding the per-GPS-fix
// setData perf trap flagged in the 2026-05-16 audit.

import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import { getMapInstance } from '../MapView';
import { ensureSvgImages } from '../iconRegistry';

// ── Constants ────────────────────────────────────────────────────────────────

export const OWNSHIP_SOURCE_ID = 'plate-overlay-ownship';
export const OWNSHIP_LAYER_ID = 'plate-overlay-ownship-layer';

// ── SVG sprite ────────────────────────────────────────────────────────────────

/** Plain SVG for the ownship arrow (pointing north), suitable for map.addImage(). */
const PLATE_OWNSHIP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><polygon points="17,3 28,29 17,23 6,29" fill="#ff5a1f" stroke="#0b0d0f" stroke-width="1.5"/><circle cx="17" cy="20" r="2" fill="#0b0d0f"/></svg>`;

/** Ensure the plate-ownship sprite is registered in the map's image atlas. */
function ensurePlateOwnshipSprite(map: MlMap): Promise<void> {
  if (map.hasImage('plate-ownship-arrow')) return Promise.resolve();
  return ensureSvgImages(map, [
    { id: 'plate-ownship-arrow', svg: PLATE_OWNSHIP_SVG, size: 34 },
  ], { defaultSize: 34, logTag: '[PlateOverlay]' });
}

// ── Ownship rendering ────────────────────────────────────────────────────────

/**
 * Add or update the ownship symbol layer on top of the plate raster.
 * headingDeg may be 0 when ground speed = 0 (no GPS heading).
 */
export function addPlateOwnshipToMap(lat: number, lon: number, headingDeg: number): void {
  const map = getMapInstance() as MlMap | null;
  if (!map) return;
  const feature = {
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [lon, lat] },
    properties: { heading: headingDeg },
  };
  const fc = { type: 'FeatureCollection' as const, features: [feature] };
  if (map.getSource(OWNSHIP_SOURCE_ID)) {
    (map.getSource(OWNSHIP_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
  } else {
    ensurePlateOwnshipSprite(map).then(() => {
      if (!map.getSource(OWNSHIP_SOURCE_ID)) {
        map.addSource(OWNSHIP_SOURCE_ID, { type: 'geojson', data: fc });
        map.addLayer({
          id: OWNSHIP_LAYER_ID,
          type: 'symbol',
          source: OWNSHIP_SOURCE_ID,
          layout: {
            'icon-image': 'plate-ownship-arrow',
            'icon-size': 1,
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
      } else {
        // Source was added between the check and the sprite load; just setData.
        (map.getSource(OWNSHIP_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
      }
    }).catch((err: unknown) => {
      console.warn('[PlateOverlay] ownship sprite load failed', err);
    });
  }
}

export function removePlateOwnshipFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  if (map.getLayer(OWNSHIP_LAYER_ID)) map.removeLayer(OWNSHIP_LAYER_ID);
  if (map.getSource(OWNSHIP_SOURCE_ID)) map.removeSource(OWNSHIP_SOURCE_ID);
}
